interface SubjectLike {
  getUrlSlug?: () => string;
}

interface GuideLevelLike {
  getId?: () => string;
  getSubject?: () => SubjectLike | undefined;
}

/**
 * The minimal structural shape these helpers read off a question set (a `QuestionSetModelV1`).
 * Typed (rather than `any`) so a future getter rename fails at compile time instead of silently
 * returning `undefined`/`[]`. Members are optional because callers pass models whose guide levels
 * may not be loaded on every surface's query.
 */
interface QuestionSetLike {
  getSubject?: () => SubjectLike | undefined;
  getGuideLevels?: () => {
    first?: () => GuideLevelLike | undefined;
    toArray?: () => GuideLevelLike[];
  } | undefined;
}

type MaybeQuestionSet = QuestionSetLike | null | undefined;

/**
 * URL for a question's assessment guide — the subject's Assessments tab (`/:subjectSlug/assessments`),
 * where guide-level restrictions are managed. Reads the subject slug from the question set, falling
 * back to its first guide level; `undefined` when unavailable so the CTA degrades instead of throwing.
 */
export const getQuestionSetGuideLevelHref = (questionSet: MaybeQuestionSet): string | undefined => {
  const subjectSlug =
    questionSet?.getSubject?.()?.getUrlSlug?.() ||
    questionSet?.getGuideLevels?.()?.first?.()?.getSubject?.()?.getUrlSlug?.();

  return subjectSlug ? `/${subjectSlug}/assessments` : undefined;
};

/** The guide-level id (lock `sourceId`) for a question set; `undefined` when guide levels aren't loaded. */
export const getQuestionSetGuideLevelId = (questionSet: MaybeQuestionSet): string | undefined =>
  questionSet?.getGuideLevels?.()?.first?.()?.getId?.();

/**
 * Every guide-level id a question set is aligned to. The alignment chain includes ancestors, so a
 * lock on ANY of these restricts the question — the indicator then shows the same state under a
 * parent and its children. Mirrors the backend's `exists(unfiltered_guide_levels, id == source_id)`
 * rule. `[]` when guide levels aren't loaded.
 */
export const getQuestionSetGuideLevelIds = (questionSet: MaybeQuestionSet): string[] =>
  (questionSet?.getGuideLevels?.()?.toArray?.() ?? [])
    .map((guideLevel) => guideLevel?.getId?.())
    .filter((id): id is string => Boolean(id));
